import _ from 'lodash';

import { BYTES_PER_KB } from 'constants/file';
import {
  TEAM_CHAT_MENTION_MAX_SUGGESTIONS,
  TEAM_CHAT_MENTION_SYMBOL,
  TeamChatMemberIdType,
  TeamChatMessagePayloadType,
} from 'constants/teamChat';
import { getScrollBehavior } from 'utils/motion';

import { TEAM_CHAT_SLASH_COMMANDS } from './TeamChat.config';
import {
  TEAM_CHAT_MENTION_TOKEN_REGEX,
  TEAM_CHAT_MENTION_TRIGGER_REGEX,
  TEAM_CHAT_SLASH_MAX_SUGGESTIONS,
  TEAM_CHAT_SLASH_TRIGGER_REGEX,
} from './TeamChat.constants';
import { entityTokensToPlainText, parseEntityTokens } from './TeamChat.entityReferences.utils';

const pick = (source, ...keys) => {
  if (!source) {
    return null;
  }

  const match = keys.find(key => !_.isNil(source[key]));

  return match ? source[match] : null;
};

export const getChannelId = channel =>
  pick(channel, 'id', 'Id', 'chatId', 'ChatId') ??
  pick(channel?.connectionInfo ?? channel?.ConnectionInfo, 'chatId', 'ChatId');

export const getChannelDisplayName = channel =>
  pick(channel, 'name', 'Name', 'displayName', 'DisplayName', 'title', 'Title') ?? '';

export const getChannelLoanHeader = channel => pick(channel, 'loanHeader', 'LoanHeader') ?? null;

const INTERNAL_CHAT_ID_SUFFIX = '_internal';
const TPO_CHAT_ID_SUFFIX = '_tpo';

// Loan-scoped chat ids are `{applicationId}{suffix}` — the internal (employee) chat and the TPO
// (broker) chat both carry the application id (DEV-5531). Global chats are not loan-scoped.
const LOAN_CHAT_ID_SUFFIXES = [INTERNAL_CHAT_ID_SUFFIX, TPO_CHAT_ID_SUFFIX];

export const getApplicationIdFromChannelId = chatId => {
  if (!chatId) {
    return null;
  }

  const suffix = LOAN_CHAT_ID_SUFFIXES.find(candidate => chatId.endsWith(candidate));

  if (!suffix) {
    return null;
  }

  return chatId.slice(0, -suffix.length) || null;
};

// The TPO chat is the one thread an EXTERNAL party (the broker) reads and writes — callers decorate
// it so users always know which of a loan's chats is visible outside the company.
export const isExternalChannelId = chatId => !!chatId && chatId.endsWith(TPO_CHAT_ID_SUFFIX);

// Canonical forward mapping (the inverse of getApplicationIdFromChannelId above) — entity-reference
// consumers OUTSIDE the drawer (DEV-5448 badges/rail/discuss-in-chat) need this without importing
// from the loan-editor-page-scoped LoanEditorChatDrawer.utils.
export const getInternalChatId = applicationId =>
  applicationId ? `${applicationId}${INTERNAL_CHAT_ID_SUFFIX}` : null;

// Forward mapping for the broker-visible TPO chat, mirroring getInternalChatId. Shared (not left in
// LoanEditorChatDrawer.utils) so entity-reference consumers OUTSIDE the drawer — the DEV-5591
// "Discuss in broker chat" row action — can target it without importing from the loan-editor page.
export const getTpoChatId = applicationId =>
  applicationId ? `${applicationId}${TPO_CHAT_ID_SUFFIX}` : null;

export const getChannelMembers = channel => channel?.members ?? channel?.Members ?? [];

export const getChannelRowTypingLabel = ({ typingUsers, members, currentUserId }) => {
  if (!typingUsers) {
    return null;
  }

  const entries = Object.entries(typingUsers);

  if (!entries.length) {
    return null;
  }

  const nicknamesById = new Map();

  (members ?? []).forEach(member => {
    const memberId = getMemberId(member);

    if (memberId) {
      nicknamesById.set(memberId, getMemberNickname(member));
    }
  });

  const otherNicknames = [];

  entries.forEach(([, info]) => {
    const userId = info?.memberId?.userId ?? info?.memberId?.UserId ?? null;

    if (!userId || userId === currentUserId) {
      return;
    }

    const nickname = nicknamesById.get(userId) ?? null;

    if (nickname) {
      otherNicknames.push(nickname);
    }
  });

  if (!otherNicknames.length) {
    return null;
  }

  if (otherNicknames.length === 1) {
    return `${otherNicknames[0]} is typing…`;
  }

  return `${otherNicknames.length} people are typing…`;
};

export const getChannelUnreadCount = channel =>
  pick(channel, 'unreadMessageCount', 'UnreadMessageCount') ?? 0;

const getChannelPinnedAt = channel => pick(channel, 'pinnedAt', 'PinnedAt');

export const isChannelPinned = channel => !!getChannelPinnedAt(channel);

const getChannelHistoryMessages = channel => {
  const chatHistory = pick(channel, 'chatHistory', 'ChatHistory');

  return pick(chatHistory, 'messages', 'Messages') ?? [];
};

export const getChannelLastMessage = channel => {
  const historyMessages = getChannelHistoryMessages(channel);
  const historyLast = historyMessages.length ? historyMessages[historyMessages.length - 1] : null;
  const topLevel = pick(channel, 'lastMessage', 'LastMessage') ?? null;

  if (!historyLast) {
    return topLevel;
  }

  if (!topLevel) {
    return historyLast;
  }

  const historyCreated = pick(historyLast, 'created', 'Created');
  const topLevelCreated = pick(topLevel, 'created', 'Created');

  if (topLevelCreated && historyCreated && topLevelCreated > historyCreated) {
    return topLevel;
  }

  return historyLast;
};

export const getChatMemberIdentifier = memberId => {
  if (!memberId) {
    return null;
  }

  return (
    pick(memberId, 'userId', 'UserId') ??
    pick(memberId, 'phoneNumber', 'PhoneNumber') ??
    pick(memberId, 'aiPersonaId', 'AiPersonaId') ??
    null
  );
};

export const getMemberId = member => {
  const direct = pick(member, 'userId', 'UserId');

  if (direct) {
    return direct;
  }

  const memberId = member?.memberId ?? member?.MemberId ?? null;
  const fromMemberId = getChatMemberIdentifier(memberId);

  if (fromMemberId) {
    return fromMemberId;
  }

  const nested = member?.id ?? member?.Id;

  if (_.isString(nested)) {
    return nested;
  }

  return getChatMemberIdentifier(nested);
};

export const getMemberChatUserId = member => {
  const direct = pick(member, 'chatUserId', 'ChatUserId');

  if (direct) {
    return direct;
  }

  const idField = member?.id ?? member?.Id;

  if (_.isString(idField)) {
    return idField;
  }

  return pick(idField, 'value', 'Value');
};

export const resolveCurrentChatUserId = (members, currentUserId) => {
  if (!currentUserId) {
    return null;
  }

  const match = (members ?? []).find(member => getMemberId(member) === currentUserId);

  return match ? getMemberChatUserId(match) : null;
};

export const getMemberNickname = member =>
  pick(member, 'nickname', 'Nickname', 'displayName', 'DisplayName', 'name', 'Name') ?? '';

// The Chats API falls back to a member's raw id string as its "name" when it can't resolve a
// display name server-side (notably for a sub-company/broker caller, who is structurally
// forbidden from the Accounts /api/users enrichment the resolver otherwise uses) — DEV-4226.
const RAW_MEMBER_ID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const isRawMemberIdentifier = value => !!value && RAW_MEMBER_ID_REGEX.test(value);

// Overrides a channel member's display name with a caller-supplied known name (e.g. the loan's
// role-assignee names, already loaded client-side) whenever the member's own name is missing or
// is just its raw id — never touches a member that already has a resolved, human name.
export const resolveKnownMemberDisplayNames = (members, knownNamesByUserId = {}) =>
  (members ?? []).map(member => {
    const memberId = getMemberId(member);
    const knownName = memberId ? knownNamesByUserId[memberId] : null;
    const currentNickname = getMemberNickname(member);

    if (!knownName || (currentNickname && !isRawMemberIdentifier(currentNickname))) {
      return member;
    }

    return { ...member, nickname: knownName };
  });

export const getChannelLastMessageTime = channel => {
  const direct = pick(channel, 'lastMessageAt', 'LastMessageAt');

  if (direct) {
    return direct;
  }

  const lastMessage = getChannelLastMessage(channel);

  return pick(lastMessage, 'created', 'Created');
};

export const sortChannelsByLastMessageTime = (channels = []) =>
  [...channels].sort((a, b) => {
    const aTime = new Date(getChannelLastMessageTime(a) ?? 0).getTime();
    const bTime = new Date(getChannelLastMessageTime(b) ?? 0).getTime();

    return bTime - aTime;
  });

export const getMentionQueryAtCaret = (text, caretPosition) => {
  if (_.isNil(text) || _.isNil(caretPosition)) {
    return null;
  }

  const before = text.slice(0, caretPosition);
  const match = before.match(TEAM_CHAT_MENTION_TRIGGER_REGEX);

  if (!match) {
    return null;
  }

  return {
    query: match[1] ?? '',
    start: match.index,
    end: caretPosition,
  };
};

export const filterMentionCandidates = ({
  members,
  currentUserId,
  query,
  priorityMemberIds = [],
}) => {
  const normalizedQuery = (query ?? '').toLowerCase();
  const prioritySet = new Set((priorityMemberIds ?? []).filter(Boolean));

  const filtered = (members ?? []).filter(member => {
    const memberId = getMemberId(member);

    if (!memberId || memberId === currentUserId) {
      return false;
    }

    if (member?.isActive === false) {
      return false;
    }

    const nickname = getMemberNickname(member).toLowerCase();

    if (!nickname) {
      return false;
    }

    if (!normalizedQuery) {
      return true;
    }

    return nickname.split(' ').some(word => word.startsWith(normalizedQuery));
  });

  return filtered
    .sort((a, b) => {
      const aPriority = prioritySet.has(getMemberId(a)) ? 0 : 1;
      const bPriority = prioritySet.has(getMemberId(b)) ? 0 : 1;

      if (aPriority !== bPriority) {
        return aPriority - bPriority;
      }

      return getMemberNickname(a).localeCompare(getMemberNickname(b));
    })
    .slice(0, TEAM_CHAT_MENTION_MAX_SUGGESTIONS);
};

export const getSlashCommandQueryAtStart = (text, caretPosition) => {
  if (_.isNil(text) || _.isNil(caretPosition)) {
    return null;
  }

  if (text.indexOf(' ') !== -1 && caretPosition > text.indexOf(' ')) {
    return null;
  }

  const match = text.match(TEAM_CHAT_SLASH_TRIGGER_REGEX);

  if (!match) {
    return null;
  }

  return {
    query: match[1] ?? '',
    start: 0,
    end: text.length,
  };
};

export const filterSlashCommandCandidates = ({ query }) => {
  const normalizedQuery = (query ?? '').toLowerCase();

  if (!normalizedQuery) {
    return TEAM_CHAT_SLASH_COMMANDS.slice(0, TEAM_CHAT_SLASH_MAX_SUGGESTIONS);
  }

  return TEAM_CHAT_SLASH_COMMANDS.filter(command => command.id.startsWith(normalizedQuery)).slice(
    0,
    TEAM_CHAT_SLASH_MAX_SUGGESTIONS,
  );
};

export const getSlashCommandId = command => command?.id ?? null;

export const replaceSlashCommandAtStart = ({ text, commandRange, command }) => {
  if (!commandRange || !command) {
    return { text, caret: text?.length ?? 0 };
  }

  const insertion = command.insertion ?? '';
  const remainder = text.slice(commandRange.end);
  const next = `${insertion}${remainder}`;

  return { text: next, caret: insertion.length };
};

export const replaceMentionAtCaret = ({ text, mentionRange, member }) => {
  if (!mentionRange || !member) {
    return { text, caret: text?.length ?? 0 };
  }

  const nickname = getMemberNickname(member);
  const insertion = `${TEAM_CHAT_MENTION_SYMBOL}${nickname} `;
  const next = `${text.slice(0, mentionRange.start)}${insertion}${text.slice(mentionRange.end)}`;

  return { text: next, caret: mentionRange.start + insertion.length };
};

// Tokenizes a plain-text run for @mentions. `baseOffset` keeps generated keys aligned to the
// ORIGINAL message text position (not the substring's local position) so keys stay unique and
// stable once the text has already been carved into segments by parseEntityTokens.
const tokenizeMentionText = (text, { members, baseOffset }) => {
  const nicknames = new Set(
    (members ?? []).map(member => getMemberNickname(member).toLowerCase()).filter(Boolean),
  );

  const matches = Array.from(text.matchAll(TEAM_CHAT_MENTION_TOKEN_REGEX));

  const { tokens, lastIndex } = matches.reduce(
    (acc, match) => {
      const matchStart = match.index ?? 0;
      const nextTokens = [...acc.tokens];

      if (matchStart > acc.lastIndex) {
        nextTokens.push({
          type: 'text',
          value: text.slice(acc.lastIndex, matchStart),
          key: `text-${baseOffset + acc.lastIndex}`,
        });
      }

      const nickname = (match[1] ?? '').toLowerCase();
      const isMention = nicknames.has(nickname);

      nextTokens.push({
        type: isMention ? 'mention' : 'text',
        value: match[0],
        key: `${isMention ? 'mention' : 'text'}-${baseOffset + matchStart}`,
      });

      return { tokens: nextTokens, lastIndex: matchStart + match[0].length };
    },
    { tokens: [], lastIndex: 0 },
  );

  if (lastIndex < text.length) {
    tokens.push({
      type: 'text',
      value: text.slice(lastIndex),
      key: `text-${baseOffset + lastIndex}`,
    });
  }

  return tokens;
};

// Entity-reference tokens (`[[type:id|label]]`) are carved out first via parseEntityTokens, then
// each remaining plain-text segment is tokenized for @mentions exactly as before — so a message
// with no entity tokens produces byte-identical output to the pre-DEV-5446 implementation.
export const renderMessageTextTokens = (text, { members } = {}) => {
  if (!text) {
    return [];
  }

  return parseEntityTokens(text).flatMap(segment => {
    if (segment.type === 'entityRef') {
      return [
        {
          type: 'entityRef',
          key: segment.key,
          entityType: segment.entityType,
          entityId: segment.entityId,
          label: segment.label,
        },
      ];
    }

    return tokenizeMentionText(segment.value, { members, baseOffset: segment.start });
  });
};

export const getMessageId = message => {
  const raw = pick(message, 'id', 'Id', 'messageId', 'MessageId');

  if (_.isString(raw) || _.isNumber(raw)) {
    return raw;
  }

  return pick(raw, 'value', 'Value');
};

export const isSystemMessage = message => {
  if (!message) {
    return false;
  }

  // Realtime messages carry the discriminated sender on `senderId`; the REST message list carries it
  // on `memberId` (DEV-5517) — a ViaSystem on either marks the system-message presentation.
  const senderId = pick(message, 'senderId', 'SenderId') ?? pick(message, 'memberId', 'MemberId');
  const senderType = pick(senderId, 'type', 'Type');

  return senderType === TeamChatMemberIdType.VIA_SYSTEM;
};

export const getMessageSender = (message, members = []) => {
  const rawMemberId = message?.memberId ?? message?.MemberId ?? null;
  const inlineSenderId = pick(message, 'senderId', 'SenderId');

  const userId =
    getChatMemberIdentifier(rawMemberId) ??
    (_.isString(inlineSenderId) ? inlineSenderId : getChatMemberIdentifier(inlineSenderId));

  const inlineNickname =
    pick(message, 'senderNickname', 'SenderNickname', 'senderName', 'SenderName') ?? '';

  const resolvedNickname = inlineNickname || (userId ? resolveMemberNickname(members, userId) : '');

  // Never surface a raw member-id GUID as a sender name. The chat member payload carries a human display
  // name for every viewer — including a sub-company (broker) user, for whom the name is resolved server-side
  // under a system context (DEV-4226) — but if a name is genuinely absent (e.g. an unresolved member, or a
  // realtime message that arrived before the member list refreshed), drop to '' so callers render their
  // neutral fallback label ('Unknown' / 'Member') instead of leaking the raw user-id GUID.
  const nickname = isRawMemberIdentifier(resolvedNickname) ? '' : resolvedNickname;

  return {
    userId: userId ?? null,
    nickname,
  };
};

const resolveMemberNickname = (members, userId) => {
  const match = (members ?? []).find(member => getMemberId(member) === userId);

  return match ? getMemberNickname(match) : '';
};

export const getMessageTimestamp = message =>
  pick(message, 'created', 'Created', 'createdAt', 'CreatedAt', 'timestamp', 'Timestamp');

export const getMessageUpdated = message => pick(message, 'updated', 'Updated');

const getMessagePayload = message => message?.payload ?? message?.Payload ?? null;

const getMessagePayloadType = message => {
  const payload = getMessagePayload(message);

  return pick(payload, 'type', 'Type');
};

export const getMessageText = message => {
  const payload = getMessagePayload(message);

  if (_.isString(payload)) {
    return payload;
  }

  return (
    pick(payload, 'message', 'Message') ?? pick(message, 'message', 'Message', 'text', 'Text') ?? ''
  );
};

export const getMessageFile = message => {
  const payload = getMessagePayload(message);

  if (!payload) {
    return null;
  }

  const fileName = pick(payload, 'fileName', 'FileName');
  const blobUrl = pick(payload, 'blobUrl', 'BlobUrl');

  if (!fileName && !blobUrl) {
    return null;
  }

  return {
    fileName: fileName ?? '',
    blobUrl: blobUrl ?? '',
    contentType: pick(payload, 'contentType', 'ContentType') ?? '',
    sizeBytes: pick(payload, 'sizeBytes', 'SizeBytes') ?? 0,
  };
};

export const getMessageReactions = message => {
  const reactions = pick(message, 'reactions', 'Reactions');

  return Array.isArray(reactions) ? reactions : [];
};

export const getReactionEmoji = reaction => pick(reaction, 'emoji', 'Emoji') ?? '';

export const getReactionChatUserId = reaction => pick(reaction, 'chatUserId', 'ChatUserId') ?? null;

export const getReactionId = reaction =>
  pick(reaction, 'id', 'Id', 'reactionId', 'ReactionId') ?? null;

export const isFileMessage = message =>
  getMessagePayloadType(message) === TeamChatMessagePayloadType.FILE;

const FILE_SIZE_PRECISION = 1;

export const formatFileSize = bytes => {
  if (!_.isFinite(bytes) || bytes <= 0) {
    return '';
  }

  if (bytes < BYTES_PER_KB) {
    return `${bytes} B`;
  }

  const kb = bytes / BYTES_PER_KB;

  if (kb < BYTES_PER_KB) {
    return `${kb.toFixed(FILE_SIZE_PRECISION)} KB`;
  }

  const mb = kb / BYTES_PER_KB;

  if (mb < BYTES_PER_KB) {
    return `${mb.toFixed(FILE_SIZE_PRECISION)} MB`;
  }

  const gb = mb / BYTES_PER_KB;

  return `${gb.toFixed(FILE_SIZE_PRECISION)} GB`;
};

const TEAM_CHAT_MESSAGE_HASH_PREFIX = 'teamchat';

export const getTeamChatMessageDomId = messageId =>
  messageId ? `teamchat-message-${messageId}` : null;

export const truncateMessagePreview = (rawText, maxChars) => {
  const text = entityTokensToPlainText(rawText ?? '');

  if (!text) {
    return '';
  }

  if (text.length <= maxChars) {
    return text;
  }

  return `${text.slice(0, maxChars - 1)}…`;
};

export const scrollToTeamChatMessage = messageId => {
  if (typeof document === 'undefined' || !messageId) {
    return;
  }

  const element = document.getElementById(getTeamChatMessageDomId(messageId));

  if (!element) {
    return;
  }

  element.scrollIntoView({ behavior: getScrollBehavior(), block: 'center' });
};

const TEAM_CHAT_SUBJECT_REF_LOAN_FIELD = 'LoanField';

export const getMessageSubjectRef = message => {
  const raw = pick(message, 'subjectRef', 'SubjectRef');

  if (!raw) {
    return null;
  }

  const type = pick(raw, 'type', 'Type');

  if (type !== TEAM_CHAT_SUBJECT_REF_LOAN_FIELD) {
    return null;
  }

  const path = pick(raw, 'path', 'Path');

  if (!path) {
    return null;
  }

  return { type, path };
};

export const getMessageReplyToId = message => pick(message, 'replyToMessageId', 'ReplyToMessageId');

export const getReplyCountByParent = messages => {
  const counts = {};

  (messages ?? []).forEach(message => {
    const replyToId = getMessageReplyToId(message);

    if (!replyToId) {
      return;
    }

    counts[replyToId] = (counts[replyToId] ?? 0) + 1;
  });

  return counts;
};

export const getCurrentViewLink = () => {
  if (typeof window === 'undefined') {
    return '';
  }

  return window.location.href ?? '';
};

export const buildTeamChatMessagePermalink = ({ chatId, messageId }) => {
  if (!chatId || !messageId || typeof window === 'undefined') {
    return '';
  }

  const { origin, pathname } = window.location;

  return `${origin}${pathname}#${TEAM_CHAT_MESSAGE_HASH_PREFIX}/${chatId}/m/${messageId}`;
};

export const parseTeamChatMessageHash = hash => {
  if (!hash) {
    return null;
  }

  const normalized = hash.startsWith('#') ? hash.slice(1) : hash;
  const parts = normalized.split('/');

  const TEAM_CHAT_HASH_EXPECTED_PARTS = 4;

  if (
    parts.length !== TEAM_CHAT_HASH_EXPECTED_PARTS ||
    parts[0] !== TEAM_CHAT_MESSAGE_HASH_PREFIX ||
    parts[2] !== 'm'
  ) {
    return null;
  }

  return { chatId: parts[1], messageId: parts[3] };
};
